import React, { useRef, useState } from "react";
import { api } from "@/lib/api";
import { Image as ImageIcon, EyeOff, Globe, Lock, AlertCircle, X } from "lucide-react";
import { toast } from "sonner";

function ImagePreview({ src, onRemove }) {
  return (
    <div className="relative mt-3 brutal-card overflow-hidden p-0 inline-block">
      <img src={src} alt="" className="max-h-56" />
      <button type="button" onClick={onRemove}
        className="absolute top-2 right-2 brutal-btn bg-card p-1" data-testid="remove-image-btn">
        <X size={14} strokeWidth={2.5} />
      </button>
    </div>
  );
}

function ModPreviewNote({ preview, onApply }) {
  if (!preview?.flag) return null;
  return (
    <div className="mt-3 brutal-card bg-accent p-3 text-sm flex items-start gap-2">
      <AlertCircle size={16} strokeWidth={2.5} className="mt-0.5" />
      <div>
        <b>Care AI:</b> {preview.reason}
        {preview.suggestion && (
          <div className="mt-1">Try: <i>&ldquo;{preview.suggestion}&rdquo;</i>
            <button type="button" className="ml-2 underline" data-testid="apply-suggestion-btn"
              onClick={onApply}>use this</button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function ComposePost({ onPosted, communityId }) {
  const [content, setContent] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [visibility, setVisibility] = useState("public");
  const [anonymous, setAnonymous] = useState(false);
  const [busy, setBusy] = useState(false);
  const [modPreview, setModPreview] = useState(null);
  const fileRef = useRef(null);

  const pick = () => fileRef.current?.click();

  const uploadImage = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    setImagePath(r.data.path);
    setImagePreview(URL.createObjectURL(file));
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try { await uploadImage(f); } catch { toast.error("Upload failed"); }
  };

  const previewMod = async () => {
    if (!content.trim()) return;
    try {
      const r = await api.post("/moderate", { content });
      setModPreview(r.data);
      if (r.data.flag) toast.warning("Care AI suggests a softer rewording — see preview.");
      else toast.success("All good — your post reads kind.");
    } catch { toast.error("Could not check"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() && !imagePath) return;
    setBusy(true);
    try {
      const r = await api.post("/posts", {
        content, image_path: imagePath, visibility, anonymous, community_id: communityId || null,
      });
      onPosted?.(r.data);
      setContent(""); setImagePath(null); setImagePreview(null); setAnonymous(false); setModPreview(null);
      if (r.data.moderation?.flag) {
        toast.warning("Posted — but care AI flagged it. Reflect on the gentle note shown above your post.");
      } else {
        toast.success("Posted to your HAEVEN");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not post");
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="brutal-card p-5 bg-card mb-6" data-testid="compose-post">
      <textarea data-testid="compose-textarea" value={content} onChange={(e) => setContent(e.target.value)}
        rows={3} maxLength={5000} placeholder="What's something kind worth sharing?"
        className="brutal-input w-full px-4 py-3 resize-none text-base" />

      {imagePreview && (
        <ImagePreview src={imagePreview} onRemove={() => { setImagePath(null); setImagePreview(null); }} />
      )}

      <ModPreviewNote preview={modPreview} onApply={() => { setContent(modPreview.suggestion); setModPreview(null); }} />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp" hidden onChange={onFile} data-testid="image-file-input" />
        <button type="button" onClick={pick} className="brutal-btn bg-card px-3 py-2 text-sm flex items-center gap-2" data-testid="add-image-btn">
          <ImageIcon size={16} strokeWidth={2.5} /> Image
        </button>
        <button type="button" onClick={() => setAnonymous(!anonymous)} data-testid="toggle-anon-btn"
          className={`brutal-btn px-3 py-2 text-sm flex items-center gap-2 ${anonymous ? "bg-foreground text-background" : "bg-card"}`}>
          <EyeOff size={16} strokeWidth={2.5} /> {anonymous ? "Anonymous" : "Public name"}
        </button>
        <select value={visibility} onChange={(e) => setVisibility(e.target.value)} className="brutal-input px-3 py-2 text-sm" data-testid="visibility-select">
          <option value="public">🌍 Public</option>
          <option value="friends">👥 Friends only</option>
          <option value="private">🔒 Just me</option>
        </select>
        <div className="flex-1" />
        <button type="button" onClick={previewMod} className="brutal-btn bg-accent px-3 py-2 text-sm" data-testid="check-tone-btn">Check tone</button>
        <button type="submit" disabled={busy || (!content.trim() && !imagePath)} className="brutal-btn bg-primary text-primary-foreground px-4 py-2 disabled:opacity-50" data-testid="submit-post-btn">
          {busy ? "Posting…" : "Post"}
        </button>
      </div>
    </form>
  );
}
